import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiEye, FiEdit2, FiTrash2, FiUserPlus, FiSearch } from "react-icons/fi";
import employeeService from "../../services/employeeService";
import Pagination from "../../components/common/Pagination";
import ConfirmModal from "../../components/common/ConfirmModal";

const STATUS_COLORS = {
  Active: { bg: "#dcfce7", color: "#166534" },
  Inactive: { bg: "#e5e7eb", color: "#475569" },
  "On Leave": { bg: "#e8f1f8", color: "#002747" },
  Resigned: { bg: "#fee2e2", color: "#b91c1c" },
};

const STATUS_OPTIONS = ["Active", "Inactive", "On Leave", "Resigned"];

export default function EmployeeList() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [employeeCategoryFilter, setEmployeeCategoryFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeeService.getAll({
        search,
        page,
        limit: 15,
        status: statusFilter,
        employeeCategory: employeeCategoryFilter,
        department: departmentFilter,
        designation: designationFilter,
      });
      setEmployees(res.data);
      setPagination(res.pagination);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [search, page, statusFilter, employeeCategoryFilter, departmentFilter, designationFilter]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);
  useEffect(() => { setPage(1); }, [search, statusFilter, employeeCategoryFilter, departmentFilter, designationFilter]);

  const clearFilters = () => {
    setSearch(""); setStatusFilter(""); setEmployeeCategoryFilter("");
    setDepartmentFilter(""); setDesignationFilter("");
  };

  const hasActiveFilters = Boolean(search || statusFilter || employeeCategoryFilter || departmentFilter || designationFilter);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await employeeService.remove(deleteTarget._id);
      toast.success(`"${deleteTarget.fullName}" deleted`);
      setDeleteTarget(null);
      fetchEmployees();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const departmentOptions = [...new Set(employees.map((e) => e.department).filter(Boolean))];
  const designationOptions = [...new Set(employees.map((e) => e.designation).filter(Boolean))];
  const employeeCategoryOptions = [...new Set(employees.map((e) => e.employeeCategory).filter(Boolean))];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Employees</h1>
          <p className="page-subtitle">{pagination?.total ?? 0} employees in the management register</p>
        </div>
        <Link to="/employees/add" className="btn btn-primary">
          <FiUserPlus size={14} /> Add Employee
        </Link>
      </div>

      <div className="card">
        {/* ── Filters ── */}
        <div style={{ padding: "0.75rem 1rem", borderBottom: "1px solid var(--border)", display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
          <div className="search-bar">
            <FiSearch size={14} color="#9ca3af" />
            <input
              placeholder="Search by name, code, mobile, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className="form-control" style={{ width: 150, fontSize: 13 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="form-control" style={{ width: 170, fontSize: 13 }} value={employeeCategoryFilter} onChange={(e) => setEmployeeCategoryFilter(e.target.value)}>
            <option value="">All Categories</option>
            {employeeCategoryOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="form-control" style={{ width: 180, fontSize: 13 }} value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
            <option value="">All Departments</option>
            {departmentOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="form-control" style={{ width: 180, fontSize: 13 }} value={designationFilter} onChange={(e) => setDesignationFilter(e.target.value)}>
            <option value="">All Designations</option>
            {designationOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {hasActiveFilters && (
            <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters}>Clear Filters</button>
          )}
        </div>

        {/* ── Table ── */}
        {loading ? (
          <div className="loading-center"><div className="spinner" />Loading employees...</div>
        ) : employees.length === 0 ? (
          <div className="empty-state">
            <p>{search ? "No employees match your search." : "No employees found."}</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Employee</th>
                  <th>Code</th>
                  <th>Category</th>
                  <th>Department</th>
                  <th>Designation</th>
                  <th>Qualification</th>
                  <th>Status</th>
                  <th>Mobile</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, index) => {
                  const sc = STATUS_COLORS[emp.status] || STATUS_COLORS.Active;
                  return (
                    <tr key={emp._id}>
                      <td style={{ color: "var(--text-muted)", fontSize: 12 }}>{(page - 1) * 15 + index + 1}</td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                          <div style={{ width: 44, height: 44, borderRadius: 12, overflow: "hidden", background: "#e2e8f0", color: "#475569", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                            {emp.photo?.url
                              ? <img src={emp.photo.url} alt={emp.fullName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              : emp.fullName.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("")}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600 }}>{emp.fullName}</div>
                            <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>{emp.email || "No email"}</div>
                          </div>
                        </div>
                      </td>
                      <td><span className="badge badge-blue">{emp.employeeCode}</span></td>
                      <td>{emp.employeeCategory || "—"}</td>
                      <td>{emp.department}</td>
                      <td>{emp.designation}</td>
                      <td>{emp.qualification || "—"}</td>
                      <td>
                        <span style={{ background: sc.bg, color: sc.color, padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 500 }}>
                          {emp.status}
                        </span>
                      </td>
                      <td>{emp.mobile || "—"}</td>
                      <td>
                        <div style={{ display: "flex", gap: "0.35rem", justifyContent: "flex-end" }}>
                          <button className="btn btn-outline btn-sm" title="View Profile" onClick={() => navigate(`/employees/${emp._id}`)}>
                            <FiEye size={12} /> View
                          </button>
                          <button className="btn btn-outline btn-sm" title="Edit Employee" onClick={() => navigate(`/employees/${emp._id}/edit`)}>
                            <FiEdit2 size={12} /> Edit
                          </button>
                          <button className="btn btn-danger btn-sm" title="Delete Employee" onClick={() => setDeleteTarget(emp)}>
                            <FiTrash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <Pagination pagination={pagination} onPageChange={setPage} />
      </div>

      {deleteTarget && (
        <ConfirmModal
          title="Delete Employee"
          message={`Delete "${deleteTarget.fullName}"? Existing attendance rows for this employee will be removed.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
    </div>
  );
}